import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  flexRender,
  SortDirection,
  type HeaderContext,
  type SortingState,
} from '@tanstack/react-table';
import { Stack } from 'common/ui/Layout';
import { Ellipsis } from 'common/ui/Ellipsis';
import type { ReactElement, ReactNode } from 'react';
import { Colors } from 'styles';
import { RightDrawerTableItemType } from './types';

const SORT_STATES: Record<string, SortingState[]> = {
  name: [[{ id: 'name', desc: false }], [{ id: 'name', desc: true }]],
  lastUsed: [
    [
      { id: 'lastUsed', desc: false },
      { id: 'name', desc: false },
    ],
    [
      { id: 'lastUsed', desc: true },
      { id: 'name', desc: false },
    ],
  ],
};

function getNextSortingState(
  columnId: keyof typeof SORT_STATES,
  direction: SortDirection | false
): SortingState {
  const states = SORT_STATES[columnId];
  if (!states?.length) return [];

  const index = direction === 'asc' ? 0 : direction === 'desc' ? 1 : -1;
  const nextIndex = (index + 1) % states.length;
  return states[nextIndex] ?? [];
}

type RightDrawerTableSortableColumnHeaderProps<TData> = {
  headerContext: HeaderContext<RightDrawerTableItemType, unknown>;
  label: ReactNode;
  onSetSorting: (next: SortingState) => void;
};

export const RightDrawerTableSortableColumnHeader = <TData,>({
  headerContext,
  label,
  onSetSorting,
}: RightDrawerTableSortableColumnHeaderProps<TData>): ReactElement => {
  const { column } = headerContext;
  const canSort = column.getCanSort();
  const isPrimarySort = column.getSortIndex() === 0;
  const sortDirection = column.getIsSorted();

  const primarySortDirection = isPrimarySort ? sortDirection : false;

  const toggleSort = () => {
    const columnId = column.id;
    if (!SORT_STATES[columnId]) return;

    const next = getNextSortingState(columnId, primarySortDirection);
    onSetSorting(next);
  };

  return (
    <Stack alignX="between" css={canSort ? 'cursor: pointer;' : ''} onClick={toggleSort}>
      <Ellipsis data-testid={`header-ellipsis-${column.id}`}>
        {flexRender(label, headerContext)}
      </Ellipsis>
      {canSort && (
        <Stack alignX="center" vertical>
          <ExpandLess
            data-testid={`sort-icon-asc-${column.id}`}
            style={{
              fontSize: '1.5rem',
              color: primarySortDirection === 'asc' ? Colors.blue3 : Colors.gray6,
            }}
          />
          <ExpandMore
            data-testid={`sort-icon-desc-${column.id}`}
            style={{
              fontSize: '1.5rem',
              marginTop: '-6px',
              color: primarySortDirection === 'desc' ? Colors.blue3 : Colors.gray6,
            }}
          />
        </Stack>
      )}
    </Stack>
  );
};
